import React, { forwardRef, useState } from 'react'
import { Card, Text, Stack, Box, Heading, Button, Spinner, Flex } from '@sanity/ui'
import { ObjectInputProps, useFormValue } from 'sanity'

const BuyerGuideContextPreview = forwardRef<HTMLDivElement, ObjectInputProps>(
  (props, ref) => {
    // Get document ID using Sanity v4 hook
    const rawId = useFormValue(['_id']) as string | undefined
    const documentId = rawId?.replace('drafts.', '')

    const [loading, setLoading] = useState(false)
    const [context, setContext] = useState<any>(null)
    const [error, setError] = useState('')

    const handleLoadContext = async () => {
      if (!documentId) {
        setError('Document must be saved first')
        return
      }

      setLoading(true)
      setError('')
      setContext(null)

      const baseURL = process.env.SANITY_STUDIO_FE_URL || 'http://localhost:3000'
      try {
        const res = await fetch(`${baseURL}/api/ai/context-preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ buyerGuideId: documentId }),
        })

        if (!res.ok) {
          const data = await res.json()
          throw new Error(data.message || 'Failed to fetch context')
        }

        const data = await res.json()
        setContext(data)
      } catch (err: any) {
        setError(err.message || 'Error loading context')
      } finally {
        setLoading(false)
      }
    }

    return (
      <Stack gap={4} ref={ref}>
        <Card padding={4} radius={2} shadow={1}>
          <Stack gap={3}>
            <Heading size={1}>AI Context Preview</Heading>
            <Text size={1} muted>
              Preview the context data that will be sent to AI generation.
            </Text>

            <Button
              text={loading ? 'Loading...' : 'Load Context'}
              tone="primary"
              onClick={handleLoadContext}
              disabled={loading || !documentId}
            />

            {!documentId && (
              <Text size={1} muted>
                Save the document first to preview context.
              </Text>
            )}
          </Stack>
        </Card>

        {error && (
          <Card padding={3} radius={2} tone="critical">
            <Text size={1}>{error}</Text>
          </Card>
        )}

        {loading && (
          <Card padding={4} radius={2}>
            <Flex align="center" justify="center" gap={3}>
              <Spinner />
              <Text size={1}>Loading context...</Text>
            </Flex>
          </Card>
        )}

        {context && (
          <Card padding={4} radius={2} shadow={1}>
            <Stack gap={3}>
              <Flex align="center" justify="space-between">
                <Heading size={1}>Context Data</Heading>
                <Text size={0} muted>
                  {context.metadata?.fitmentTypesCount} fitment types, {context.metadata?.totalListings} listings
                </Text>
              </Flex>
              <Box
                padding={3}
                style={{
                  backgroundColor: '#1a1a1a',
                  borderRadius: '4px',
                  maxHeight: '500px',
                  overflow: 'auto'
                }}
              >
                <pre style={{ margin: 0, fontSize: '11px', color: '#e0e0e0', whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(context.context, null, 2)}
                </pre>
              </Box>
            </Stack>
          </Card>
        )}
      </Stack>
    )
  }
)

BuyerGuideContextPreview.displayName = 'BuyerGuideContextPreview'

export default BuyerGuideContextPreview
